import { useState } from "react";
import { PublicPageShell, PublicPageCta } from "./PublicPageShell";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

/**
 * HowItWorksPage — MACRO #26 · Increment 3 (`#how-it-works`).
 *
 * Beginner-proof rework: the six real steps keep their hero + card rhythm, but
 * each card now carries an INDEPENDENT inline toggle ("How this works") that
 * expands an extremely simple, step-by-step explanation inside the card (no
 * modals, no navigation). Supporting sections — Before you start, The complete
 * journey, Draft vs Published, How The Grid is experienced and a beginner FAQ —
 * explain the EXISTING product only. Every user-facing string is sourced from
 * the i18n layer (English canonical); icons/structure are not translated.
 */
const STEP_ICONS: JSX.Element[] = [
  (
    <svg key="step-icon-0" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M13.5 13.5 17 17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  (
    <svg key="step-icon-1" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <rect x="3" y="3" width="14" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3 8h14M8 3v14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    </svg>
  ),
  (
    <svg key="step-icon-2" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <rect x="3" y="4" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 8h5M6 11h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  (
    <svg key="step-icon-3" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path d="M10 2 3 6v8l7 4 7-4V6z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M10 18V10M3 6l7 4 7-4" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
    </svg>
  ),
  (
    <svg key="step-icon-4" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path d="M10 15V5M10 5 6 9M10 5l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 16h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  (
    <svg key="step-icon-5" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 3v14M3 10h14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    </svg>
  ),
];

const STYLE = `
  @keyframes howReveal { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
  .how-reveal { animation: howReveal .22s cubic-bezier(0.16,1,0.3,1); }
  @media (prefers-reduced-motion: reduce) { .how-reveal { animation: none; } }
`;

export function HowItWorksPage() {
  const { dict, t } = useI18n();
  const how = dict.how;

  return (
    <PublicPageShell
      testId="how-it-works-page"
      index="H · 02"
      eyebrow={t("how.eyebrow")}
      title={
        <>
          {how.titleLine1}
          <br />
          {how.titleLine2}
        </>
      }
      intro={how.intro}
      footer={<PublicPageCta />}
    >
      <style>{STYLE}</style>

      {/* BEFORE YOU START */}
      <section data-testid="how-before" className="mb-14 md:mb-20">
        <SectionHeader eyebrow={how.before.eyebrow} title={how.before.title} intro={how.before.intro} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {how.before.items.map((item, i) => (
            <article
              key={item.title}
              data-testid={`how-before-item-${i}`}
              className="neon-glass p-5 flex flex-col gap-2"
            >
              <span className="font-mono text-[10.5px] font-semibold tracking-[0.24em] text-neon-teal">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display text-[13.5px] font-semibold tracking-[0.02em] text-text-primary">
                {item.title}
              </h3>
              <p className="font-display text-[12.5px] leading-[1.6] text-text-secondary">
                {item.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* SIX STEPS — each with an independent inline toggle */}
      <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 list-none p-0 m-0">
        {how.steps.map((step, i) => (
          <StepCard key={step.title} index={i} step={step} icon={STEP_ICONS[i]} />
        ))}
      </ol>

      {/* THE COMPLETE JOURNEY */}
      <section data-testid="how-journey" className="mt-16 md:mt-20">
        <SectionHeader eyebrow={how.journey.eyebrow} title={how.journey.title} intro={how.journey.intro} />
        <div className="neon-glass p-6 md:p-8">
          <ol className="flex flex-col sm:flex-row sm:flex-wrap sm:items-stretch gap-3 list-none p-0 m-0">
            {how.journey.steps.map((label, i) => {
              const optional = i === 3;
              return (
                <li
                  key={label}
                  data-testid={`how-journey-step-${i}`}
                  className="flex items-center gap-3 sm:flex-1 sm:min-w-[130px]"
                >
                  <div className="flex-1 rounded-md border border-border-soft bg-obsidian/50 px-3 py-2.5">
                    <div className="font-mono text-[9.5px] font-semibold tracking-[0.22em] text-neon-teal">
                      {String(i + 1).padStart(2, "0")}
                      {optional && (
                        <span className="ml-1.5 text-text-muted lowercase tracking-[0.14em]">
                          · {how.journey.optional}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 font-display text-[12.5px] font-semibold tracking-[0.02em] text-text-primary whitespace-nowrap">
                      {label}
                    </div>
                  </div>
                  {i < how.journey.steps.length - 1 && (
                    <svg
                      aria-hidden
                      className="shrink-0 text-neon-teal/60 rotate-90 sm:rotate-0"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* DRAFT VS PUBLISHED */}
      <section data-testid="how-draft" className="mt-16 md:mt-20">
        <SectionHeader eyebrow={how.draft.eyebrow} title={how.draft.title} intro={how.draft.intro} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          <ExplainerPanel
            testId="how-draft-draft"
            label={how.draft.draftLabel}
            body={how.draft.draftBody}
            tone="draft"
          />
          <ExplainerPanel
            testId="how-draft-published"
            label={how.draft.publishedLabel}
            body={how.draft.publishedBody}
            tone="published"
          />
        </div>
        <p
          data-testid="how-draft-takeaway"
          className="mt-4 flex items-start gap-2.5 font-display text-[13px] leading-[1.6] text-text-secondary"
        >
          <span aria-hidden className="mt-1.5 h-1.5 w-1.5 rotate-45 bg-neon-teal shadow-glow-teal-sm shrink-0" />
          {how.draft.takeaway}
        </p>
      </section>

      {/* HOW THE GRID IS EXPERIENCED */}
      <section data-testid="how-levels" className="mt-16 md:mt-20">
        <SectionHeader eyebrow={how.levels.eyebrow} title={how.levels.title} intro={how.levels.intro} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {how.levels.items.map((lvl, i) => (
            <article
              key={lvl.title}
              data-testid={`how-level-${i}`}
              className="relative neon-glass p-6 flex flex-col gap-2 overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute -right-3 -top-4 font-hero text-[64px] leading-none text-neon-teal/10 select-none"
              >
                {i + 1}
              </span>
              <span className="font-mono text-[9.5px] font-semibold tracking-[0.22em] uppercase text-neon-teal">
                {lvl.tag}
              </span>
              <h3 className="font-display text-[15px] font-semibold tracking-[0.02em] text-text-primary">
                {lvl.title}
              </h3>
              <p className="font-display text-[13px] leading-[1.6] text-text-secondary">
                {lvl.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* BEGINNER FAQ */}
      <section data-testid="how-faq" className="mt-16 md:mt-20">
        <SectionHeader eyebrow={how.faq.eyebrow} title={how.faq.title} intro={how.faq.intro} />
        <div className="flex flex-col gap-3 max-w-[820px]">
          {how.faq.items.map((item, i) => (
            <FaqRow key={item.q} index={i} q={item.q} a={item.a} />
          ))}
        </div>
      </section>
    </PublicPageShell>
  );
}

/* ─────────────────────────── building blocks ─────────────────────────── */

function SectionHeader({ eyebrow, title, intro }: { eyebrow: string; title: string; intro?: string }) {
  return (
    <header className="mb-6 md:mb-8 max-w-[720px]">
      <div className="flex items-center gap-3">
        <span aria-hidden className="h-2 w-2 rotate-45 bg-neon-teal shadow-glow-teal-sm" />
        <span className="font-mono text-[10.5px] tracking-[0.28em] uppercase text-neon-teal">
          {eyebrow}
        </span>
      </div>
      <h2 className="mt-3 font-hero uppercase text-text-primary text-[26px] md:text-[34px] leading-[0.98] tracking-[0.01em]">
        {title}
      </h2>
      {intro && (
        <p className="mt-3 font-display text-[13.5px] leading-[1.65] text-text-secondary">
          {intro}
        </p>
      )}
    </header>
  );
}

type Step = {
  title: string;
  body: string;
  detail: { intro: string; points: string[] };
};

function StepCard({ index, step, icon }: { index: number; step: Step; icon: JSX.Element }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const n = String(index + 1);
  const detailId = `how-step-${n}-detail`;

  return (
    <li data-testid={`how-step-${n}`} className="neon-glass p-6 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "inline-flex items-center justify-center h-9 w-9 rounded-md",
            "border border-border-accent text-neon-teal bg-obsidian/60",
          )}
        >
          {icon}
        </span>
        <span className="font-mono text-[11px] font-semibold tracking-[0.24em] text-text-muted">
          {t("how.stepLabel", { n })}
        </span>
      </div>
      <h3 className="font-display text-[15px] font-semibold tracking-[0.02em] text-text-primary">
        {step.title}
      </h3>
      <p className="flex-1 font-display text-[13px] leading-[1.6] text-text-secondary">
        {step.body}
      </p>

      <button
        type="button"
        data-testid={`how-step-${n}-toggle`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={detailId}
        aria-label={t("how.toggle.aria")}
        className={cn(
          "mt-1 inline-flex items-center gap-2 self-start",
          "font-mono text-[10.5px] font-semibold tracking-[0.16em] uppercase",
          "text-neon-teal hover:opacity-80 transition-opacity duration-fast",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] rounded-sm",
        )}
      >
        {open ? t("how.toggle.hide") : t("how.toggle.show")}
        <span
          aria-hidden
          className={cn(
            "inline-flex items-center justify-center h-4 w-4 rounded-full border border-border-accent",
            "transition-transform duration-fast",
            open && "rotate-45",
          )}
        >
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
            <path d="M5 1.5v7M1.5 5h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      {open && (
        <div
          id={detailId}
          data-testid={detailId}
          className="how-reveal mt-1 rounded-md border border-border-divider bg-obsidian/40 p-4 flex flex-col gap-3"
        >
          <p className="font-display text-[12.5px] leading-[1.6] text-text-primary">
            {step.detail.intro}
          </p>
          <ol className="flex flex-col gap-2 list-none p-0 m-0">
            {step.detail.points.map((point, pi) => (
              <li key={pi} className="flex items-start gap-2.5">
                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border-accent font-mono text-[8.5px] font-semibold text-neon-teal">
                  {pi + 1}
                </span>
                <span className="font-display text-[12.5px] leading-[1.55] text-text-secondary">
                  {point}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </li>
  );
}

function ExplainerPanel({
  testId,
  label,
  body,
  tone,
}: {
  testId: string;
  label: string;
  body: string;
  tone: "draft" | "published";
}) {
  const isPublished = tone === "published";
  return (
    <article
      data-testid={testId}
      className={cn(
        "neon-glass p-6 flex flex-col gap-2.5 relative overflow-hidden",
        isPublished && "shadow-[0_0_0_1px_var(--border-strong)] shadow-glow-teal-md",
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-sm border",
            isPublished ? "border-border-accent text-neon-teal" : "border-border-soft text-text-muted",
          )}
        >
          {isPublished ? (
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 8l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M9 2.5l2.5 2.5L5 11.5 2.5 9 9 2.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span
          className={cn(
            "font-display text-[13px] font-semibold tracking-[0.16em] uppercase",
            isPublished ? "text-neon-teal" : "text-text-primary",
          )}
        >
          {label}
        </span>
      </div>
      <p className="font-display text-[13px] leading-[1.6] text-text-secondary">{body}</p>
    </article>
  );
}

function FaqRow({ index, q, a }: { index: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  const answerId = `how-faq-${index}-a`;
  return (
    <div data-testid={`how-faq-${index}`} className="neon-glass overflow-hidden">
      <button
        type="button"
        data-testid={`how-faq-${index}-q`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={answerId}
        className={cn(
          "w-full flex items-center justify-between gap-4 px-5 py-4 text-left",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
        )}
      >
        <span className="font-display text-[13.5px] font-semibold tracking-[0.01em] text-text-primary">
          {q}
        </span>
        <span
          aria-hidden
          className={cn(
            "shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full border border-border-accent text-neon-teal",
            "transition-transform duration-fast",
            open && "rotate-45",
          )}
        >
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
            <path d="M5 1.5v7M1.5 5h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && (
        <div id={answerId} data-testid={answerId} className="how-reveal px-5 pb-4 -mt-1">
          <p className="font-display text-[13px] leading-[1.65] text-text-secondary">{a}</p>
        </div>
      )}
    </div>
  );
}
